import { FC, useEffect, useState } from "react";
import localStorageService from "service/localStorageService";
import {
    DesktopDateTimePicker as DateTimePicker,
    LocalizationProvider,
} from "@mui/lab";
import AdapterDatetime from "@mui/lab/AdapterMoment";
import { TextField } from "@mui/material";
import moment, { Moment } from "moment-timezone";
import { getLocalTimezone } from "utils/functions";
import { convertToTimezone, getInputMask } from "utils/dateTimeConversion";
import {
    DATE_TIME_RANGE_EARLY_MESSAGE,
    DATE_TIME_RANGE_INVALID_MESSAGE,
} from "constant";
import { Row } from "react-bootstrap";
import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
import { getValidDateTimeFormats } from "utils/datetime";

type DateTimeRangePickerProps = {
    value?: Moment[];
    onChange?: (value: Moment[]) => void;
    onError?: (error: string) => void;
    className?: string;
    timezone?: string;
    inline?: boolean;
};

const DateTimeRangePicker: FC<DateTimeRangePickerProps> = ({
    value,
    timezone,
    onChange,
    onError,
    inline,
}) => {
    const { dateTimeFormat: InputDateTimeFormat, is12HourFormat } =
        useDateTimeFormatStore((state) => ({
            dateTimeFormat: state.dateTimeFormat,
            is12HourFormat: state.is12HourFormat,
        }));
    const defaultTimeZone = getLocalTimezone();
    const timeZoneObject = localStorageService.getItem("timezones");

    const [startDateTime, setStartDateTime] = useState(
        moment.utc().subtract(24, "hours").tz(defaultTimeZone)
    );
    const [endDateTime, setEndDateTime] = useState(
        moment.utc().tz(defaultTimeZone)
    );

    const [minDate, setMinDate] = useState(
        moment.utc("2022-01-01").tz(defaultTimeZone)
    );
    const [errMsg, setErrMsg] = useState("");
    const [dateError, setDateError] = useState({ start: "", end: "" });
    const _timezone = timezone ?? defaultTimeZone;

    useEffect(() => {
        if (value && value.length > 0) {
            setStartDateTime(value[0]);
            setEndDateTime(value[1]);
        }
    }, [value]);

    useEffect(() => {
        const start = moment.tz(+startDateTime, _timezone);
        const end = moment(moment.tz(+endDateTime, _timezone));

        setStartDateTime(start);
        setEndDateTime(end);
        setMinDate(moment.tz(+minDate, _timezone));

        onChange?.([start, end]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timezone]);

    useEffect(() => {
        validate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDateTime, endDateTime, minDate]);

    useEffect(() => {
        const hasDateError = !!dateError.start || !!dateError.end;
        const combinedError = hasDateError
            ? dateError.start || dateError.end
            : errMsg;
        onError?.(combinedError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateError, errMsg]);

    const validateAndParseDateTime = (
        input: string,
        field: "start" | "end"
    ): { isValid: boolean; parsedDate?: Moment } => {
        if (!input) {
            setDateError((prev) => ({
                ...prev,
                [field]: "Date and time are required",
            }));
            return { isValid: false };
        }

        const validFormats = getValidDateTimeFormats(is12HourFormat);

        for (const format of validFormats) {
            const parsed = moment.tz(input, format, true, _timezone);
            if (parsed.isValid()) {
                setDateError((prev) => ({ ...prev, [field]: "" }));
                return { isValid: true, parsedDate: parsed };
            }
        }

        setDateError((prev) => ({
            ...prev,
            [field]: `Invalid format. Expected: ${InputDateTimeFormat}`,
        }));
        return { isValid: false };
    };

    const handleStartDateChange = (newValue?: Moment | null) => {
        if (newValue && moment.isMoment(newValue) && newValue.isValid()) {
            const value = convertToTimezone(
                newValue,
                _timezone,
                timeZoneObject
            );
            setStartDateTime(value);
            setDateError((prev) => ({ ...prev, start: "" }));
            onChange?.([value, endDateTime]);
        }
    };

    const handleEndDateChange = (newValue?: Moment | null) => {
        if (newValue && moment.isMoment(newValue) && newValue.isValid()) {
            const value = convertToTimezone(
                newValue,
                _timezone,
                timeZoneObject
            );
            setEndDateTime(value);
            setDateError((prev) => ({ ...prev, end: "" }));
            onChange?.([startDateTime, value]);
        }
    };

    const validate = () => {
        let message = "";
        const now = moment.utc().tz(_timezone);

        if (startDateTime.isBefore(minDate)) {
            message = DATE_TIME_RANGE_EARLY_MESSAGE.fill({
                time: minDate.format(InputDateTimeFormat),
            });
        } else if (!endDateTime.isAfter(startDateTime, "minutes")) {
            message = DATE_TIME_RANGE_INVALID_MESSAGE;
        } else if (endDateTime.isAfter(now, "minutes")) {
            message = "End time must be before future time";
        }

        setErrMsg(message);
        onError?.(message);
    };

    const render = () => (
        <Row>
            <div className={`dateTimeFieldWrapper ${inline ? "inline" : ""}`}>
                <div className="dateTimeField">From</div>
                <DateTimePicker
                    renderInput={(props) => (
                        <TextField
                            {...props}
                            label=""
                            InputLabelProps={{
                                shrink: false,
                            }}
                            InputProps={{
                                ...props.InputProps,
                                inputProps: {
                                    ...props?.inputProps,
                                    "aria-label": "start-time",
                                },
                            }}
                            onChange={(e) => {
                                const result = validateAndParseDateTime(
                                    e.target.value,
                                    "start"
                                );

                                if (result.isValid && result.parsedDate) {
                                    handleStartDateChange(result.parsedDate);
                                }

                                props.onChange?.(e);
                            }}
                            error={!!dateError.start}
                        />
                    )}
                    inputFormat={InputDateTimeFormat}
                    value={startDateTime}
                    onChange={(newValue: any) => {
                        handleStartDateChange(newValue);
                    }}
                    mask={getInputMask(is12HourFormat)}
                    minDateTime={minDate}
                    maxDateTime={endDateTime}
                />
            </div>

            <div className={`dateTimeFieldWrapper ${inline ? "inline" : ""}`}>
                <div className="dateTimeField">To</div>
                <DateTimePicker
                    renderInput={(props) => (
                        <TextField
                            {...props}
                            label=""
                            InputLabelProps={{
                                shrink: false,
                            }}
                            InputProps={{
                                ...props.InputProps,
                                inputProps: {
                                    ...props?.inputProps,
                                    "aria-label": "end-time",
                                },
                            }}
                            onChange={(e) => {
                                const result = validateAndParseDateTime(
                                    e.target.value,
                                    "end"
                                );

                                if (result.isValid && result.parsedDate) {
                                    handleEndDateChange(result.parsedDate);
                                }

                                props.onChange?.(e);
                            }}
                            error={!!dateError.end}
                        />
                    )}
                    inputFormat={InputDateTimeFormat}
                    value={endDateTime}
                    onChange={(newValue: any) => {
                        handleEndDateChange(newValue);
                    }}
                    mask={getInputMask(is12HourFormat)}
                    minDateTime={startDateTime}
                />
            </div>

            {(errMsg || dateError.start || dateError.end) && (
                <div
                    className={`dateTimeFieldWrapper ${inline ? "w-100" : ""}`}
                >
                    <div
                        className={`dateTimeField ${inline ? "d-none" : ""}`}
                    />
                    <div>
                        <p role="alert" className="text-danger">
                            {dateError.start || dateError.end || errMsg}
                        </p>
                    </div>
                </div>
            )}
        </Row>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDatetime}>
            {render()}
        </LocalizationProvider>
    );
};

export default DateTimeRangePicker;
